import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { SKIP_SPINNER_HEADER } from '../shared/http-interceptor/http-interceptor';

import {
  IApiServiceConfig, ITokenResponseData, ICreateUserRequest, ICreateUserResponse, IGetJobTokenResponse,
  IGetIsNameAvailableResponse, IResetUserPasswordRequest, IUser, IPagedResponse, IRole, IResetUserPasswordResponse,
  IJob, IVehicle, IDevice, IAssignUserToDeviceRequest, IAddUserToJobRequest, IAddResetPasswordCodeResponse, IAssignRoleToUserRequest,
  IRefreshTokenResponse, IGetDeviceLivePositionsForJobResponse, IGetDeviceLogPositionsForJobResponse,
  IGetDevicesLastLivePositionForJobRequest, IGetIsPAIPAddressAvailableResponse, ISetting,
  ICreateEntityResponse, IDeviceUserWithUsername, ISearchJobsRequest,
  IUpdateDeviceRequest, IUpdateEntityResponse, IUserProfile, IDeviceWithVehicle, IGetDeviceLivePositionsForJobRequest,
  IGetDevicesLastLivePositionForJobResponse, IPermission, IUpdateSettingsResponse, ISearchUsersRequest,
  IGetDeviceLogPositionsForJobRequest, IGetDeviceRecordsCountWithMinMaxDatesRequest, IGetLastConnectionActivitiesRequest,
  IGetLastConnectionActivitiesResponse, ISearchDevicesRequest, IGetDeviceRecordsCountWithMinMaxDatesResponse,
  IGetConnectionEventIPAddressRequest, IGetServerInfoResponse, ITrackingServerDiagnostics, ISearchTrackingServerDiagnosticsRequest,IGetUserProfileSettingsResponse,
  IGetDeviceAnalysisSectionsResponse, IClientSettings, IReverseGeocodeResult, IGetApiRequestIPAddressRequest, IGetApiRequestIPAddressResponse,
  IGetConnectionEventIPAddressResponse, IGetDeviceAnalysisSectionsRequest, IGetLastApiUserActivitiesResponse, IPagingParameters,
  IGetJobVehicleWithDeviceAssignmentsResponse, IUpdateDeviceToVehicleAssignmentResponse, IDeleteDeviceToVehicleAssignmentResponse, IDateRange,
  IGetDeviceAnalysisSectionDataRequest, IGetDeviceAnalysisSectionDataResponse, IDeviceToVehicleAssignment,
  IAddDeviceToVehicleAssignmentResponse, IUserWithRoles, IGetCurrentJobConfigurationResponse, IGetJobDevicesWithCurrentVehicleResponse,
  ISetEncryptionKeysRequest, IGetEncryptionKeysResponse, IUploadEncryptionKeysExportDataResponse, IUploadEncryptionKeysExportDataRequest,
  ISearchDeviceEventsRequest, IDeviceEvent, IDeviceFilterGroup, IUpdateUserProfileSettingsRequest,IGetAnalysisRendezvousResponse, IGetAnalysisRendezvousRequest,
  IArchiveJobResponse,IJobArchive,UploadArchiveResponse
} from './interfaces';
import { ApiUtils } from './api-utils';
import { IStringNameWithStringValue } from './internal-interfaces';
import { ObservablesService, INetworkError, TokenService, StorageKey, SessionStorageService, DownloadFormat } from '../shared';

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private state: IApiServiceState;

  constructor(
    private httpClient: HttpClient, private utilsSvc: ApiUtils, private obsSvc: ObservablesService,
    private tokenSvc: TokenService, private sessStorageSvc: SessionStorageService
  ) {
    this.state = {} as IApiServiceState;
  }

  init(config: IApiServiceConfig) {
    this.state.config = config;
    this.state.authorizationHeaderValue = '';
    this.state.formUrlEncodedHeader = { 'Content-Type': 'application/x-www-form-urlencoded' };
    this.utilsSvc.init(this.state.config.baseApiUrl);
  }

  async searchDeviceEvents(
    pagingParameters: IPagingParameters, req: ISearchDeviceEventsRequest
  ): Promise<IPagedResponse<IDeviceEvent>> {
    const url = this.utilsSvc.getSearchDeviceEventsUrl();
    const res = await this.postWithQuery<IPagedResponse<IDeviceEvent>>(url, req, this.pagingParametersToQueryParameters(pagingParameters));
    return res;
  }

  async acknowledgeDeviceEvent(deviceEventId: string): Promise<IUpdateEntityResponse> {
    const url = this.utilsSvc.getAcknowledgeDeviceEventUrl(deviceEventId);
    const res = await this.post<IUpdateEntityResponse>(url, {});
    return res;
  }

  async acknowledgeAllDeviceEvents(): Promise<IUpdateEntityResponse> {
    const url = this.utilsSvc.getAcknowledgeAllDeviceEventsUrl();
    const res = await this.post<IUpdateEntityResponse>(url, {});
    return res;
  }

  async uploadEncryptionKeysExportedData(req: IUploadEncryptionKeysExportDataRequest): Promise<IUploadEncryptionKeysExportDataResponse> {
    const url = this.utilsSvc.getUploadEncryptionKeysExportedDataUrl();
    const res = await this.put<IUploadEncryptionKeysExportDataResponse>(url, req);
    return res;
  }

  async getEncryptionKeys(): Promise<IGetEncryptionKeysResponse> {
    const url = this.utilsSvc.getGetEncryptionKeysUrl();
    const res = await this.getWithHeaders<IGetEncryptionKeysResponse>(url, { [SKIP_SPINNER_HEADER]: 'true' });
    return res;
  }

  async setEncryptionKeys(req: ISetEncryptionKeysRequest): Promise<void> {
    const url = this.utilsSvc.getSetEncryptionKeysUrl();
    await this.put<void>(url, req);
  }

  async getTemporaryToken(): Promise<ITokenResponseData> {
    const url = this.utilsSvc.getGetTemporaryTokenUrl();
    const res = await this.get<ITokenResponseData>(url);
    return res;
  }

  getDeviceAnalysisSectionDataAsFileUrl(jobId: string, format: DownloadFormat, trackColor: string, req: IGetDeviceAnalysisSectionDataRequest, token: string): string {
    const url = this.utilsSvc.getGetDeviceAnalysisSectionDataAsFileUrl(jobId, format, trackColor, req, token);
    return url;
  }

  async getJobDevicesWithCurrentVehicle(jobId: string): Promise<IGetJobDevicesWithCurrentVehicleResponse> {
    const url = this.utilsSvc.getGetDevicesWithCurrentVehicle(jobId);
    const res = await this.get<IGetJobDevicesWithCurrentVehicleResponse>(url);
    return res;
  }

  async getDeviceAnalysisSectionData(jobId: string, req: IGetDeviceAnalysisSectionDataRequest): Promise<IGetDeviceAnalysisSectionDataResponse> {
    const url = this.utilsSvc.getGetDeviceAnalysisSectionDataUrl(jobId);
    const res = await this.post<IGetDeviceAnalysisSectionDataResponse>(url, req);
    return res;
  }

  async getAnalysisRendezvous(jobId: string, req: IGetAnalysisRendezvousRequest): Promise<IGetAnalysisRendezvousResponse> {
    const url = this.utilsSvc.getAnalysisRendezvousUrl(jobId);
    const res = await this.post<IGetAnalysisRendezvousResponse>(url, req);
    return res;
  }

  async getDeviceAnalysisSections(jobId: string, req: IGetDeviceAnalysisSectionsRequest): Promise<IGetDeviceAnalysisSectionsResponse> {
    const url = this.utilsSvc.getGetDeviceAnalysisSectionsUrl(jobId);
    const res = await this.post<IGetDeviceAnalysisSectionsResponse>(url, req);
    return res;
  }

  getDeviceLogPositionsForJobAsFileUrl(
    jobId: string, deviceId: string, from: string, to: string, trackColor: string, format: string, token: string
  ): string {
    return this.utilsSvc.getGetDeviceLogPositionsForJobAsFileUrl(jobId, deviceId, from, to, trackColor, format, token);
  }

  getDeviceLivePositionsForJobAsFileUrl(
    jobId: string, deviceId: string, from: string, to: string, trackColor: string, format: string, token: string
  ): string {
    return this.utilsSvc.getGetDeviceLivePositionsForJobAsFileUrl(jobId, deviceId, from, to, trackColor, format, token);
  }

  async searchTrackingServerDiagnostics(
    pagingParameters: IPagingParameters, req: ISearchTrackingServerDiagnosticsRequest
  ): Promise<IPagedResponse<ITrackingServerDiagnostics>> {
    const url = this.utilsSvc.getSearchTrackingServerDiagnosticsUrl();
    const res = await this.postWithQuery<IPagedResponse<ITrackingServerDiagnostics>>(
      url, req, this.pagingParametersToQueryParameters(pagingParameters)
    );
    return res;
  }

  // Reverse-geocodes a coordinate pair to an address.
  async reverseGeocode(lat: number, lon: number): Promise<IReverseGeocodeResult> {
    const url = this.utilsSvc.getReverseGeocodeUrl(lat, lon);
    const res = await this.getWithHeaders<IReverseGeocodeResult>(url, { [SKIP_SPINNER_HEADER]: 'true' });
    return res;
  }

  async getClientSettings(): Promise<IClientSettings> {
    const url = this.utilsSvc.getGetClientSettingsUrl();
    const res = this.get<IClientSettings>(url);
    return res;
  }

  async getServerInfo(): Promise<IGetServerInfoResponse> {
    const url = this.utilsSvc.getGetServerInfoUrl();
    const res = this.get<IGetServerInfoResponse>(url);
    return res;
  }

  async getUserProfileSettings(userId:string): Promise<IGetUserProfileSettingsResponse> {
    const url = this.utilsSvc.getUserProfileSettingsUrl(userId);
    const res = this.get<IGetUserProfileSettingsResponse>(url);
    return res;
  }

  async updateUserProfileUserTableSettings(userId: string, req: IUpdateUserProfileSettingsRequest): Promise<Promise<IUpdateEntityResponse> > {
    const url = this.utilsSvc.getUpdateUserProfileUserTableSettingsUrl(userId);
    const res = await this.patch<Promise<IUpdateEntityResponse>>(url, req);
    return res;
  }

  async getUpdateUserProfileDeviceTableSettings(userId: string, req: IUpdateUserProfileSettingsRequest): Promise<Promise<IUpdateEntityResponse> > {
    const url = this.utilsSvc.getUpdateUserProfileDeviceTableSettingsUrl(userId);
    const res = await this.patch<Promise<IUpdateEntityResponse>>(url, req);
    return res;
  }

  async getUpdateUserProfileRoleTableSettings(userId: string, req: IUpdateUserProfileSettingsRequest): Promise<Promise<IUpdateEntityResponse> > {
    const url = this.utilsSvc.getUpdateUserProfileRoleTableSettingsUrl(userId);
    const res = await this.patch<Promise<IUpdateEntityResponse>>(url, req);
    return res;
  }

  async getUpdateUserProfileMissionTableSettings(userId: string, req: IUpdateUserProfileSettingsRequest): Promise<Promise<IUpdateEntityResponse> > {
    const url = this.utilsSvc.getUpdateUserProfileMissionTableSettingsUrl(userId);
    const res = await this.patch<Promise<IUpdateEntityResponse>>(url, req);
    return res;
  }

  async getUpdateUserProfileAlarmNotificationSettings(userId: string, req: IUpdateUserProfileSettingsRequest): Promise<Promise<IUpdateEntityResponse> > {
    const url = this.utilsSvc.getUpdateUserProfileAlarmNotificationsSettingsUrl(userId);
    const res = await this.patch<Promise<IUpdateEntityResponse>>(url, req);
    return res;
  }

  async getUpdateUserProfileMissionAnalysisSettings(userId: string, req: IUpdateUserProfileSettingsRequest): Promise<Promise<IUpdateEntityResponse> > {
    const url = this.utilsSvc.getUpdateUserProfileMissionAnalysisSettingsUrl(userId);
    const res = await this.patch<Promise<IUpdateEntityResponse>>(url, req);
    return res;
  }

  async getUpdateUserProfileMissionPositionSettings(userId: string, req: IUpdateUserProfileSettingsRequest): Promise<Promise<IUpdateEntityResponse> > {
    const url = this.utilsSvc.getUpdateUserProfileMisisonPositionSettingsUrl(userId);
    const res = await this.patch<Promise<IUpdateEntityResponse>>(url, req);
    return res;
  }

  async getApiRequestIPAddress(req: IGetApiRequestIPAddressRequest): Promise<IGetApiRequestIPAddressResponse> {
    const url = this.utilsSvc.getGetApiRequestIPAddressUrl();
    const res = await this.post<IGetApiRequestIPAddressResponse>(url, req);
    return res;
  }

  async getConnectionEventIPAddress(req: IGetConnectionEventIPAddressRequest): Promise<IGetConnectionEventIPAddressResponse> {
    const url = this.utilsSvc.getGetConnectionEventIPAddressUrl();
    const res = await this.post<IGetConnectionEventIPAddressResponse>(url, req);
    return res;
  }

  async getLastApiUserActivities(): Promise<IGetLastApiUserActivitiesResponse> {
    const url = this.utilsSvc.getGetLastApiUserActivitiesUrl();
    const res = await this.get<IGetLastApiUserActivitiesResponse>(url);
    return res;
  }

  async getDeviceRecordsCountWithMinMaxDates(req: IGetDeviceRecordsCountWithMinMaxDatesRequest): Promise<IGetDeviceRecordsCountWithMinMaxDatesResponse> {
    const url = this.utilsSvc.getGetDeviceRecordsCountWithMinMaxDatesUrl();
    const res = await this.post<IGetDeviceRecordsCountWithMinMaxDatesResponse>(url, req);
    return res;
  }

  async getDeviceRecordsCountWithMinMaxDatesForDatesInThePast(req: IGetDeviceRecordsCountWithMinMaxDatesRequest): Promise<IGetDeviceRecordsCountWithMinMaxDatesResponse> {
    const url = this.utilsSvc.getGetDeviceRecordsCountWithMinMaxDatesForDatesInThePastUrl();
    const res = await this.post<IGetDeviceRecordsCountWithMinMaxDatesResponse>(url, req);
    return res;
  }

  async getDeviceRecordsCountWithMinMaxDatesForDatesInTheFuture(req: IGetDeviceRecordsCountWithMinMaxDatesRequest): Promise<IGetDeviceRecordsCountWithMinMaxDatesResponse> {
    const url = this.utilsSvc.getGetDeviceRecordsCountWithMinMaxDatesForDatesInTheFutureUrl();
    const res = await this.post<IGetDeviceRecordsCountWithMinMaxDatesResponse>(url, req);
    return res;
  }

  async getLastConnectionActivities(req: IGetLastConnectionActivitiesRequest): Promise<IGetLastConnectionActivitiesResponse> {
    const url = this.utilsSvc.getGetLastConnectionActivitiesUrl();
    const res = await this.post<IGetLastConnectionActivitiesResponse>(url, req);
    return res;
  }

  async searchDevices(req: ISearchDevicesRequest): Promise<IPagedResponse<IDevice>> {
    const url = this.utilsSvc.getSearchDevicesUrl();
    const res = await this.post<IPagedResponse<IDevice>>(url, req);
    return res;
  }

  async filterGroupDevices(req: ISearchDevicesRequest, filter:string): Promise<IPagedResponse<IDeviceFilterGroup>> {
    const url = this.utilsSvc.getFilterDevicesUrl(filter);
    const res = await this.post<IPagedResponse<IDeviceFilterGroup>>(url, req);
    return res;
  }

  async searchUsers(req: ISearchUsersRequest): Promise<IPagedResponse<IUser>> {
    const url = this.utilsSvc.getSearchUsersUrl();
    const res = await this.post<IPagedResponse<IUser>>(url, req);
    return res;
  }

  async updateSettings(settings: ISetting[]): Promise<IUpdateSettingsResponse> {
    const url = this.utilsSvc.getGetAllSettingsUrl();
    const res = await this.put<IUpdateSettingsResponse>(url, settings);
    return res;
  }

  async getAllSettings(): Promise<ISetting[]> {
    const url = this.utilsSvc.getGetAllSettingsUrl();
    const res = await this.get<ISetting[]>(url);
    return res;
  }

  async getIsPAIPAddressAvailable(paipAddress: string): Promise<IGetIsPAIPAddressAvailableResponse> {
    const url = this.utilsSvc.getGetIsPAIPAddressAvailableUrl(paipAddress);
    const res = await this.get<IGetIsPAIPAddressAvailableResponse>(url);
    return res;
  }

  async getUsersInRole(roleId: string): Promise<IUser[]> {
    const url = this.utilsSvc.getGetUsersInRoleUrl(roleId);
    const res = await this.get<IUser[]>(url);
    return res;
  }

  async getMyMissionDevices(): Promise<IDevice[]> {
    const url = this.utilsSvc.getGetMyMissionDevicesUrl();
    const res = await this.get<IDevice[]>(url);
    return res;
  }

  async getPermissions(): Promise<IPermission[]> {
    const url = this.utilsSvc.getGetPermissionsUrl();
    const res = await this.get<IPermission[]>(url);
    return res;
  }

  async getRolePermissions(roleId: string): Promise<IPermission[]> {
    const url = this.utilsSvc.getGetRolePermissionsUrl(roleId);
    const res = await this.get<IPermission[]>(url);
    return res;
  }

  async getRole(roleId: string): Promise<IRole> {
    const url = this.utilsSvc.getGetRoleUrl(roleId);
    const res = await this.get<IRole>(url);
    return res;
  }

  async getDevicesLastLivePositionForJob(jobId: string, req: IGetDevicesLastLivePositionForJobRequest): Promise<IGetDevicesLastLivePositionForJobResponse> {
    const url = this.utilsSvc.getGetDevicesLastLivePositionForJobUrl(jobId);
    const res = await this.post<IGetDevicesLastLivePositionForJobResponse>(url, req);
    return res;
  }

  async deleteDeviceToVehicleAssignment(assignmentId: string): Promise<IDeleteDeviceToVehicleAssignmentResponse> {
    const url = this.utilsSvc.getDeleteDeviceToVehicleAssignmentUrl(assignmentId);
    return await this.delete<IDeleteDeviceToVehicleAssignmentResponse>(url);
  }

  async updateDeviceToVehicleAssignment(assignmentId: string, dateRange: IDateRange): Promise<IUpdateDeviceToVehicleAssignmentResponse> {
    const url = this.utilsSvc.getUpdateDeviceToVehicleAssignmentUrl(assignmentId);
    const res = await this.patch<IUpdateDeviceToVehicleAssignmentResponse>(url, dateRange);
    return res;
  }

  async addDeviceToVehicleAssignment(assignment: IDeviceToVehicleAssignment): Promise<IAddDeviceToVehicleAssignmentResponse> {
    const url = this.utilsSvc.getAddDeviceToVehicleAssignmentUrl();
    const res = await this.post<IAddDeviceToVehicleAssignmentResponse>(url, assignment);
    return res;
  }

  async getJobListConfiguration(req: ISearchUsersRequest): Promise<IPagedResponse<IGetCurrentJobConfigurationResponse>> {
    const url = this.utilsSvc.getJobListConfigurationUrl();
    const res = await this.post<IPagedResponse<IGetCurrentJobConfigurationResponse>>(url, req);
    return res;
  }

  async getCurrentJobConfiguration(): Promise<IGetCurrentJobConfigurationResponse> {
    const url = this.utilsSvc.getGetCurrentJobConfigurationUrl();
    const res = await this.get<IGetCurrentJobConfigurationResponse>(url);
    return res;
  }

  async getJobVehiclesWithDeviceAssignmentsUrl(jobId: string): Promise<IGetJobVehicleWithDeviceAssignmentsResponse> {
    const url = this.utilsSvc.getGetJobVehiclesWithDeviceAssignmentsUrl(jobId);
    const res = await this.get<IGetJobVehicleWithDeviceAssignmentsResponse>(url);
    return res;
  }

  async getDeviceLogPositionsForJob(jobId: string, deviceId: string, req: IGetDeviceLogPositionsForJobRequest): Promise<IGetDeviceLogPositionsForJobResponse> {
    const url = this.utilsSvc.getGetDeviceLogPositionsForJobUrl(jobId, deviceId);
    const res = await this.post<IGetDeviceLogPositionsForJobResponse>(url, req);
    return res;
  }

  async getDeviceLivePositionsForJob(jobId: string, deviceId: string, req: IGetDeviceLivePositionsForJobRequest): Promise<IGetDeviceLivePositionsForJobResponse> {
    const url = this.utilsSvc.getGetDeviceLivePositionsForJobUrl(jobId, deviceId);
    const res = await this.post<IGetDeviceLivePositionsForJobResponse>(url, req);
    return res;
  }

  async refreshToken(): Promise<IRefreshTokenResponse> {
    const url = this.utilsSvc.getRefreshTokenUrl();
    const res = await this.post<IRefreshTokenResponse>(url, null, true);
    return res;
  }

  async unassignRoleFromUser(userId: string, systemRoleId: string): Promise<void> {
    const url = this.utilsSvc.getUnassignRoleFromUserUrl(userId, systemRoleId);
    await this.delete<void>(url);
  }

  async assignRoleToUser(userId: string, req: IAssignRoleToUserRequest): Promise<void> {
    const url = this.utilsSvc.getAssignRoleToUserUrl(userId);
    await this.post<void>(url, req);
  }

  async getUserRoles(userId: string): Promise<IRole[]> {
    const url = this.utilsSvc.getGetUserRolesUrl(userId);
    const res = await this.get<IRole[]>(url);
    return res;
  }

  async addUserResetPasswordCode(userId: string): Promise<IAddResetPasswordCodeResponse> {
    const url = this.utilsSvc.getAddResetPasswordCodeUrl(userId);
    const res = await this.post<IAddResetPasswordCodeResponse>(url, null);
    return res;
  }

  async updateUserProfile(userProfile: IUserProfile): Promise<void> {
    const url = this.utilsSvc.getUpdateUserProfileUrl(userProfile.userId);
    await this.put<void>(url, userProfile);
  }

  async getUserProfile(userId: string): Promise<IUserProfile> {
    const url = this.utilsSvc.getGetUserProfileUrl(userId);
    const res = await this.get<IUserProfile>(url);
    return res;
  }

  async activateUser(userId: string): Promise<void> {
    const url = this.utilsSvc.getActivateUserUrl(userId);
    await this.patch<void>(url, null);
  }

  async deactivateUser(userId: string): Promise<void> {
    const url = this.utilsSvc.getDeactivateUserUrl(userId);
    await this.patch<void>(url, null);
  }

  async getUser(userId: string): Promise<IUser> {
    const url = this.utilsSvc.getGetUserUrl(userId);
    const res = await this.get<IUser>(url);
    return res;
  }

  async getAllRolesOfTypeSystem(): Promise<IRole[]> {
    const url = this.utilsSvc.getGetAllRolesOfTypeSystemUrl();
    const res = await this.get<IRole[]>(url);
    return res;
  }

  async getAllRolesByJobContext(): Promise<IRole[]> {
    const url = this.utilsSvc.getGetAllRolesByJobContextUrl();
    const res = await this.get<IRole[]>(url);
    return res;
  }

  async getAllUsersByJobContext(): Promise<IUser[]> {
    const url = this.utilsSvc.getGetAllUsersByJobContextUrl();
    const res = await this.get<IUser[]>(url);
    return res;
  }

  async removeUserFromJob(userId: string, jobId: string): Promise<void> {
    const url = this.utilsSvc.getRemoveUserFromJobUrl(userId, jobId);
    await this.delete<void>(url);
  }

  async getJobUsers(jobId: string): Promise<IUserWithRoles[]> {
    const url = this.utilsSvc.getGetJobUsersUrl(jobId);
    const res = await this.get<IUserWithRoles[]>(url);
    return res;
  }

  async addUserToJob(jobId: string, req: IAddUserToJobRequest): Promise<void> {
    const url = this.utilsSvc.getAddUserToJobUrl(jobId);
    await this.post<void>(url, req);
  }

  /* [Direct user->Tracking Unit assignment removed]
     User access is now managed exclusively through mission assignment.

  async unassignUserFromDevice(deviceIe: string, userId: string): Promise<void> {
    const url = this.utilsSvc.getUnassignUserFromDeviceUrl(deviceIe, userId);
    await this.delete<void>(url);
  }

  async assignUserToDevice(deviceId: string, userId: string, req: IAssignUserToDeviceRequest): Promise<void> {
    const url = this.utilsSvc.getAssignUserToDeviceUrl(deviceId, userId);
    await this.post<void>(url, req);
  }

  async getDeviceUsers(deviceId: string): Promise<IDeviceUserWithUsername[]> {
    const url = this.utilsSvc.getGetDeviceUsersUrl(deviceId);
    const res = await this.get<IDeviceUserWithUsername[]>(url);
    return res;
  }
  */

  async updateDevice(deviceId: string, req: IUpdateDeviceRequest): Promise<IUpdateEntityResponse> {
    const url = this.utilsSvc.getUpdateDeviceUrl(deviceId);
    const res = await this.patch<IUpdateEntityResponse>(url, req);
    return res;
  }

  async getDevice(deviceId: string): Promise<IDevice> {
    const url = this.utilsSvc.getGetDeviceUrl(deviceId);
    const res = await this.get<IDevice>(url);
    return res;
  }

  async getDevices(): Promise<IDevice[]> {
    const url = this.utilsSvc.getGetDevicesUrl();
    const res = await this.get<IDevice[]>(url);
    return res;
  }

  async getJobDevices(jobId: string): Promise<IDevice[]> {
    const url = this.utilsSvc.getGetJobDevicesUrl(jobId);
    const res = await this.get<IDevice[]>(url);
    return res;
  }

  async createDevice(device: IDevice): Promise<ICreateEntityResponse> {
    const url = this.utilsSvc.getCreateDeviceUrl();
    const res = await this.post<ICreateEntityResponse>(url, device);
    return res;
  }

  async getJobVehicles(jobId: string): Promise<IVehicle[]> {
    const url = this.utilsSvc.getGetJobVehiclesUrl(jobId);
    const res = await this.get<IVehicle[]>(url);
    return res;
  }

  async createVehicle(vehicle: IVehicle): Promise<ICreateEntityResponse> {
    const url = this.utilsSvc.getCreateVehicleUrl();
    const res = await this.post<ICreateEntityResponse>(url, vehicle);
    return res;
  }

  async updateVehicle(vehicle: IVehicle): Promise<void> {
    const url = this.utilsSvc.getUpdateVehicleUrl();
    await this.patch<void>(url, vehicle);
  }

  async updateJob(job: IJob): Promise<void> {
    const url = this.utilsSvc.getUpdateJobUrl(job.id);
    await this.put<void>(url, job);
  }

  async archiveJob(job: IJob): Promise<IArchiveJobResponse> {
    const url = this.utilsSvc.getArchiveJobUrl(job.id);
    return this.post<IArchiveJobResponse>(url, job);
  }

  async deleteJob(job: IJob): Promise<boolean> {
    const url = this.utilsSvc.getDeleteJobUrl(job.id);
    return await this.delete<boolean>(url);
  }

  async getJobLastArchivedAt(jobId: string): Promise<Date | null> {
    const url = this.utilsSvc.getJobLastArchivedAt(jobId);
    const res = await this.get<{ jobId: string; lastArchivedAt: string | null }>(url);
    return res?.lastArchivedAt ? new Date(res.lastArchivedAt) : null;
  }

  async getJobArchivesList (req: IPagingParameters): Promise<IPagedResponse<IJobArchive>> {
    const url = this.utilsSvc.getJobArchivesListUrl();
    const res = await this.post<IPagedResponse<IJobArchive>>(url, req);
    return res;
  }
  
  async deleteJobArchive(id: string): Promise<void> {
    const url = this.utilsSvc.getDeleteJobArchiveUrl(id);
    await this.delete<void>(url);
  }

  async downloadJobArchive(archiveId: string, fileName: string): Promise<void> {
    const url = this.utilsSvc.getJobArchiveDownloadUrl(archiveId);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': this.state.authorizationHeaderValue
      }
    });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.statusText}`);
    }
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const finalFileName = fileName || 'archive.json';
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = finalFileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }

  async restoreJobArchive(archiveId: string): Promise<void> {
    const url = this.utilsSvc.getJobArchiveRestoreUrl(archiveId);
    await this.post<void>(url,{});
  }

  async uploadArchive(file: File): Promise<UploadArchiveResponse> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const url = this.utilsSvc.getUploadArchiveUrl();
    return this.post<UploadArchiveResponse>(url, formData);
  }

  async getJob(jobId: string): Promise<IJob> {
    const url = this.utilsSvc.getGetJobUrl(jobId);
    const res = await this.get<IJob>(url);
    return res;
  }

  async createJob(job: IJob): Promise<any> {
    const url = this.utilsSvc.getCreateJobUrl();
    const res = await this.post<any>(url, job);
    return res;
  }

  async searchJobs(req: ISearchJobsRequest): Promise<IPagedResponse<IJob>> {
    const url = this.utilsSvc.getSearchJobsUrl();
    const res = await this.post<IPagedResponse<IJob>>(url, req);
    return res;
  }

  async resetUserPassword(username: string, req: IResetUserPasswordRequest): Promise<IResetUserPasswordResponse> {
    const url = this.utilsSvc.getResetPasswordUrl(username);
    const res = await this.post<IResetUserPasswordResponse>(url, req);
    return res;
  }

  async createUser(req: ICreateUserRequest): Promise<ICreateUserResponse> {
    const url = this.utilsSvc.getCreateUserUrl();
    const res = this.post<ICreateUserResponse>(url, req);
    return res;
  }

  async getIsUsernameAvailable(username: string): Promise<IGetIsNameAvailableResponse> {
    const url = this.utilsSvc.getGetIsUsernameAvailableUrl(username);
    const res = await this.get<IGetIsNameAvailableResponse>(url);
    return res;
  }

  async getAllRoles(): Promise<IPagedResponse<IRole>> {
    const url = this.utilsSvc.getGetAllRolesUrl();
    const res = await this.get<IPagedResponse<IRole>>(url);
    return res;
  }

  async getAllUsers(): Promise<IPagedResponse<IUser>> {
    const url = this.utilsSvc.getGetAllUsersUrl();
    const res = await this.get<IPagedResponse<IUser>>(url);
    return res;
  }

  async getJobToken(jobId: string): Promise<IGetJobTokenResponse> {
    const url = this.utilsSvc.getGetJobTokenUrl(jobId);
    const response = await this.get<IGetJobTokenResponse>(url);
    return response;
  }

  useJobToken(response: IGetJobTokenResponse): void {
    if (response.access_token) {
      this.setAuthorizationHeaderValue(response.token_type + ' ' + response.access_token);
      const tokenInfo = this.tokenSvc.setToken(response.access_token);
      this.sessStorageSvc.setItem(StorageKey.authResponse, response);
    }
  }

  async authenticate(username: string, password: string): Promise<ITokenResponseData> {
    const url = this.utilsSvc.getAuthenticateUrl();
    const encodedUsername = encodeURIComponent(username);
    const encodedPassword = encodeURIComponent(password);
    const body = `username=${encodedUsername}&password=${encodedPassword}&grant_type=password`;
    const response = await this.postWithHeaders<ITokenResponseData>(url, body, this.state.formUrlEncodedHeader, null);
    return response;
  }

  async authenticateOtp(username: string, password: string, otp:string): Promise<ITokenResponseData> {
    const url = this.utilsSvc.getAuthenticateUrl();
    const encodedUsername = encodeURIComponent(username);
    const encodedPassword = encodeURIComponent(password);
    const body = `username=${encodedUsername}&password=${encodedPassword}&grant_type=2factor&code=${otp}`;
    const response = await this.postWithHeaders<ITokenResponseData>(url, body, this.state.formUrlEncodedHeader, null);
    return response;
  }

  setAuthorizationHeaderValue(value: string): void {
    this.state.authorizationHeaderValue = value;
  }

  clearAuthorizationData(): void {
    this.state.authorizationHeaderValue = '';
  }

  getLastRequestTimestamp(): number | undefined {
    return this.state.lastRequestTimestamp;
  }

  private async get<TResponse>(path: string): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.get, path);
    return this.request(req);
  }

  private async getWithQuery<TResponse>(path: string, queryParams: any): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.get, path, null, queryParams);
    return this.request(req);
  }

  private async getWithHeaders<TResponse>(path: string, headers: any): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.get, path, null, undefined, headers);
    return this.request(req);
  }

  private async post<TResponse>(path: string, body: any, suppressUpdatingLastRequestTimestamp?: boolean): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.post, path, body);
    return this.request(req, suppressUpdatingLastRequestTimestamp);
  }

  private async postWithQuery<TResponse>(path: string, body: any, queryParams: IQueryParameters, suppressUpdatingLastRequestTimestamp?: boolean): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.post, path, body, queryParams);
    return this.request(req, suppressUpdatingLastRequestTimestamp);
  }

  private async postWithHeaders<TResponse>(path: string, body: any, headers: any, queryParams: any): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.post, path, body, queryParams, headers);
    return this.request(req);
  }

  private async put<TResponse>(path: string, body: any): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.put, path, body);
    return this.request(req);
  }

  private async patch<TResponse>(path: string, body: any): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.patch, path, body);
    return this.request(req);
  }
  async searchUserProfileWithRoles(req: ISearchUsersRequest): Promise<IPagedResponse<IUser>> {
    const url = this.utilsSvc.getSearchUserProfileWithRolesUrl();
    const res = await this.post<IPagedResponse<IUser>>(url, req);
    return res;
  }
  private async delete<TResponse>(path: string): Promise<TResponse> {
    const req = this.createRequest(RequestMethod.delete, path);
    return this.request(req);
  }

  private getNow(): number {
    return Date.now();
  }

  private async request<TResponse>(req: HttpRequest<any>, suppressUpdatingLastRequestTimestamp?: boolean): Promise<TResponse> {
    let result: TResponse = undefined as any;
    try {
      if (!suppressUpdatingLastRequestTimestamp) {
        this.state.lastRequestTimestamp = this.getNow();
      }
      const promise = lastValueFrom(this.httpClient.request(req));
      const httpEvent = await promise;
      const httpResponse = httpEvent as HttpResponse<TResponse>;
      result = httpResponse.body as TResponse;
      // this.successSubject.next({
      //   url: req.urlWithParams,
      //   method: req.method,
      //   body: req.body,
      //   result: result,
      //   caller: caller
      // });
    } catch (err: any) {
      let errObj: INetworkError = {} as INetworkError;
      if (err instanceof HttpErrorResponse) {
        if (err.error) {
          // TODO: Convert
          errObj.error = { ...err.error };
        }
        errObj.message = err.message;
        errObj.name = err.name;
        errObj.ok = err.ok;
        errObj.status = err.status;
        errObj.statusText = err.statusText;
        errObj.url = err.url;
      }
      // const errObj = this.utilsSvc.createError(err);
      // let shouldIgnoreError = false;
      // if (errObj.communicationError && ignoreHttpErrorsList && ignoreHttpErrorsList.indexOf(errObj.communicationError.status) >= 0) {
      //   shouldIgnoreError = true;
      // }
      // if (!shouldIgnoreError) {
      //   this.errorSubject.next({
      //     error: errObj,
      //     url: req.urlWithParams,
      //     method: req.method,
      //     body: req.body,
      //     caller: caller
      //   });
      //   throw errObj;
      // }
      this.obsSvc.setNetworkResponseError(errObj);
      throw errObj;
    }
    return result;
  }

  private createRequest(
    method: RequestMethod,
    url: string,
    body?: any,
    queryParams?: { [name: string]: string | string[] },
    headers?: { [name: string]: string | string[] },
    responseType?: 'arraybuffer' | 'blob' | 'json' | 'text'
  ): HttpRequest<any> {
    const params = new HttpParams({ fromObject: queryParams });
    const finalHeaders = this.getHeaders(headers, this.state.authorizationHeaderValue);
    const req = new HttpRequest(method, url, body, {
      headers: finalHeaders,
      params: params,
      responseType: responseType,
    });
    return req;
  }

  private getHeaders(headers: IStringNameWithStringValue | undefined, authorizaionHeaderValue: string): HttpHeaders {
    const resultHeaders = { ...headers };
    if (authorizaionHeaderValue) {
      resultHeaders['Authorization'] = authorizaionHeaderValue;
    }
    const result = new HttpHeaders(resultHeaders);
    return result;
  }

  private pagingParametersToQueryParameters(pagingParameters: IPagingParameters): IQueryParameters {
    const queryParameters: IQueryParameters = {
      offset: '' + pagingParameters.offset,
      maxItems: '' + pagingParameters.maxItems
    };
    return queryParameters;
  }
}

interface IApiServiceState {
  config: IApiServiceConfig;
  authorizationHeaderValue: string;
  formUrlEncodedHeader: IStringNameWithStringValue;
  lastRequestTimestamp?: number;
}

interface IQueryParameters {
  [name: string]: string | string[];
}

const enum RequestMethod {
  get = 'GET',
  post = 'POST',
  put = 'PUT',
  patch = 'PATCH',
  delete = 'DELETE'
}
